import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Camera, Loader2, RefreshCw } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useMomentsToday } from '../../hooks/useMomentsToday'
import MomentsReveal from '../../components/MomentsReveal'

const MAX_EDGE = 1280
const JPEG_QUALITY = 0.82

// MomentsCapturePage — /moments/capture/:promptId
//
// Near-fullscreen camera: live preview, single shutter, no gallery
// fallback (the whole point of Moments is that you can't pre-bake the
// shot). On submit, transitions in-place to the stacked reveal view;
// the partner image fills in live via the hook's realtime stream.
//
// Pair-gated via hasOwnerAccess so the partner's account works too — the
// page bounces anyone else to the dashboard before camera permission
// is even requested.
export function MomentsCapturePage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { promptId } = useParams()
  const {
    prompt, mine, partner, partnerName, myImageUrl, partnerImageUrl,
    status, windowOpen, msUntilClose, submit, loading: hookLoading,
  } = useMomentsToday(promptId, { locale })

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [facingMode, setFacingMode] = useState('environment')
  const [camState, setCamState] = useState('boot') // boot|requesting|preview|denied|unavailable
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const submitted = !!mine

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop())
      streamRef.current = null
    }
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  const startCamera = useCallback(async (mode) => {
    const useMode = mode || facingMode
    stopCamera()
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCamState('unavailable')
      return
    }
    setCamState('requesting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: useMode, width: { ideal: 1920 }, height: { ideal: 1920 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        try { await videoRef.current.play() } catch { /* autoplay race — element will catch up */ }
      }
      setCamState('preview')
    } catch (e) {
      const name = e?.name || ''
      if (name === 'NotAllowedError' || name === 'SecurityError') setCamState('denied')
      else setCamState('unavailable')
    }
  }, [facingMode, stopCamera])

  // Boot the camera only when we have a usable prompt, the user has
  // not yet submitted, and the window is still open. Skipping the
  // camera request when the user has already submitted means iOS
  // doesn't re-prompt for permission on a revisit to the reveal.
  useEffect(() => {
    if (authLoading || hookLoading) return undefined
    if (!prompt || submitted || !windowOpen) return undefined
    startCamera()
    return stopCamera
  }, [authLoading, hookLoading, prompt?.id, submitted, windowOpen])

  useEffect(() => stopCamera, [stopCamera])

  const flipCamera = useCallback(async () => {
    const next = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(next)
    await startCamera(next)
  }, [facingMode, startCamera])

  const shoot = useCallback(async () => {
    if (!videoRef.current || submitting) return
    const v = videoRef.current
    const w = v.videoWidth
    const h = v.videoHeight
    if (!w || !h) return
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
    const outW = Math.round(w * scale)
    const outH = Math.round(h * scale)
    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(v, 0, 0, outW, outH)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
    if (!blob) { setError(t('moments.captureFailed')); return }

    setSubmitting(true)
    setError(null)
    stopCamera()
    const result = await submit(blob)
    setSubmitting(false)
    if (result.error) {
      setError(result.error)
      // Restart so the user can retry without re-mounting the page.
      startCamera()
    }
  }, [submit, submitting, stopCamera, startCamera, t])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || hookLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }
  if (!prompt) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <Header onBack={() => navigate('/moments')} t={t} />
        <p dir="auto" className="mt-6 text-base opacity-80">{t('moments.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/moments')}
          className="mt-4 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
        >
          {t('moments.backToIndex')}
        </button>
      </div>
    )
  }

  const minutesLeft = Math.max(0, Math.ceil(msUntilClose / 60000))

  if (submitted) {
    return (
      <div className="mx-auto max-w-md space-y-4 p-4">
        <Header onBack={() => navigate('/moments')} t={t} />
        <MomentsReveal
          mine={mine}
          partner={partner}
          myImageUrl={myImageUrl}
          partnerImageUrl={partnerImageUrl}
          partnerName={partnerName}
          windowOpen={windowOpen}
          viewerId={user?.id}
          locale={locale}
          aiCaption={prompt?.ai_caption}
          aiCaptionAr={prompt?.ai_caption_ar}
          aiTags={prompt?.ai_tags}
        />
        {status === 'revealed' && (
          <p dir="auto" className="pt-2 text-center text-xs uppercase tracking-wide opacity-60">
            {t('moments.revealed')}
          </p>
        )}
      </div>
    )
  }

  if (!windowOpen) {
    return (
      <div className="mx-auto max-w-md p-6">
        <Header onBack={() => navigate('/moments')} t={t} />
        <p dir="auto" className="mt-6 text-center text-base">{t('moments.youMissed')}</p>
      </div>
    )
  }

  if (camState === 'denied') {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <Header onBack={() => navigate('/moments')} t={t} />
        <p dir="auto" className="mt-6 text-base font-medium">{t('moments.cameraDenied')}</p>
        <p dir="auto" className="mt-2 text-sm opacity-70">{t('moments.cameraDeniedHint')}</p>
        <button
          type="button"
          onClick={() => startCamera()}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
        >
          <RefreshCw className="h-4 w-4" /> {t('moments.retry')}
        </button>
      </div>
    )
  }

  if (camState === 'unavailable') {
    return (
      <div className="mx-auto max-w-md p-6">
        <Header onBack={() => navigate('/moments')} t={t} />
        <p dir="auto" className="mt-6 text-center text-base">{t('moments.cameraUnavailable')}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md p-4">
      <Header onBack={() => navigate('/moments')} t={t} />
      <div className="relative mt-3 aspect-square overflow-hidden rounded-3xl bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-cover"
        />
        {camState === 'requesting' && (
          <div className="absolute inset-0 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
        {windowOpen && minutesLeft > 0 && (
          <div className="absolute start-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white">
            <span dir="auto">{t('moments.windowClosingIn', { minutes: minutesLeft })}</span>
          </div>
        )}
        <button
          type="button"
          onClick={flipCamera}
          className="absolute end-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white"
          aria-label={t('moments.flipCamera')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-6 flex items-center justify-center">
        <button
          type="button"
          onClick={shoot}
          disabled={camState !== 'preview' || submitting}
          className="grid h-16 w-16 place-items-center rounded-full bg-rose-500 text-white shadow-lg disabled:opacity-50"
          aria-label={t('moments.shoot')}
        >
          {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-7 w-7" />}
        </button>
      </div>
      {error && (
        <p dir="auto" className="mt-3 text-center text-sm text-rose-500">{error}</p>
      )}
    </div>
  )
}

function Header({ onBack, t }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
        aria-label={t('moments.back')}
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <h1 dir="auto" className="text-lg font-semibold">{t('moments.captureTitle')}</h1>
    </div>
  )
}
